import axios from 'axios';
import {
  getRefreshToken,
  getTokenType,
  isRefreshTokenExpired,
  removeRefreshToken,
  removeToken,
  removeTokenType,
  setRefreshToken,
  setToken,
  setTokenType,
} from '@/utils/common';

type RefreshResult = {
  success: true;
  accessToken: string;
  refreshToken: string;
  tokenType?: string;
};

type RefreshResponse = {
  content?: any;
  succeeded?: boolean;
};

type AuthFailureHandler = () => Promise<void> | void;

let isRefreshing = false;
let refreshPromise: Promise<RefreshResult> | null = null;
let authFailureHandler: AuthFailureHandler | null = null;

const extractAuthTokens = (content: any) => {
  const accessToken =
    content?.auth?.token?.accessToken || content?.token?.accessToken || content?.accessToken || null;
  const refreshToken =
    content?.auth?.token?.refreshToken ||
    content?.token?.refreshToken ||
    content?.refreshToken ||
    null;
  const tokenType =
    content?.auth?.token?.tokenType || content?.token?.tokenType || content?.tokenType || null;

  return {
    accessToken,
    refreshToken,
    tokenType,
  };
};

export function registerAuthFailureHandler(handler: AuthFailureHandler | null) {
  authFailureHandler = handler;
}

export async function handleAuthFailure() {
  removeToken();
  removeRefreshToken();
  removeTokenType();

  if (authFailureHandler) {
    await authFailureHandler();
  }
}

export async function refreshAccessToken(): Promise<RefreshResult> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;

  refreshPromise = (async () => {
    try {
      const refreshToken = getRefreshToken();
      const currentTokenType = getTokenType();

      if (!refreshToken || isRefreshTokenExpired(refreshToken)) {
        throw new Error('No valid refresh token');
      }

      const response = await axios.post<RefreshResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/auths/sessions/refresh`,
        {
          refreshToken,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      const { content, succeeded } = response.data ?? {};

      if (!succeeded || !content) {
        throw new Error('Refresh failed');
      }

      const {
        accessToken: newAccessToken,
        refreshToken: newRefreshToken,
        tokenType: nextTokenType,
      } = extractAuthTokens(content);

      if (!newAccessToken) {
        throw new Error('Refresh response does not contain access token');
      }

      const nextRefreshToken = newRefreshToken || refreshToken;
      const resolvedTokenType = nextTokenType || currentTokenType || 'Bearer';

      setToken(newAccessToken);
      setRefreshToken(nextRefreshToken);
      setTokenType(resolvedTokenType);

      return {
        success: true,
        accessToken: newAccessToken,
        refreshToken: nextRefreshToken,
        tokenType: resolvedTokenType,
      };
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}
